import type {
  ChecklistItemInput,
  Env,
  Region,
} from '@internal/backstage-plugin-bidone-backend';
import type { BDirectRow } from '../../../hooks/useBDirectRows';

// Wraps the BDirectRow shape into the storage input the backend expects.
// `state` is deliberately omitted — saves don't touch it (PATCH-only) — and
// `repo` is included for backward compatibility but ignored on the merge
// update path. Manual rows carry `id` strings the backend treats as fresh
// keys (the saveSection path uses upsert semantics).
export const toChecklistItemInput = (row: BDirectRow): ChecklistItemInput => ({
  id: row.id.startsWith('new-') ? undefined : row.id,
  kind: 'step',
  text: row.text,
  order: row.order,
  repo: row.repo,
  completed: row.completed,
});

export const newManualRow = (env: Env): BDirectRow => {
  // Client-side temporary id; backend assigns a fresh UUID on create.
  const tempId = `new-${Math.random().toString(36).slice(2, 10)}`;
  // Seed with one unchecked checkbox per region in the current env so the
  // user has something to tick. Mirrors the "manual rows are editable"
  // spec rule without forcing the user to pre-declare a region schema.
  const completed: BDirectRow['completed'] = {};
  for (const r of ['AUE', 'WEU', 'SAF', 'BRS', 'ASE', 'NEU'] as Region[]) {
    completed[r] = { [env]: false };
  }
  return {
    id: tempId,
    kind: 'step',
    text: '',
    order: 0,
    repo: null,
    state: 'not_ready',
    completed,
    lockedBy: null,
    stateUpdatedBy: null,
    updatedBy: null,
    perRegionTag: {},
    manualTicks: false,
    perRegionPreprodTag: {},
  };
};

// Synthetic placeholder section row, used when a group of steps appears
// without a leading section header (legacy data, mid-edit transient state).
// The factory keeps the shape identical to a real `BDirectRow` so the card
// component can render it without a "kind=section?" branch every time.
export const orphanSectionRow = (id: string): BDirectRow => ({
  id,
  kind: 'section',
  text: '(Orphan steps)',
  order: 0,
  repo: null,
  state: 'not_ready',
  completed: {},
  lockedBy: null,
  stateUpdatedBy: null,
  updatedBy: null,
  perRegionTag: {},
  manualTicks: false,
  perRegionPreprodTag: {},
});

// Walks the flat list of checklist items and emits one
// `{ section, steps }` block per section header. The new BDirect layout
// always has a section as the first item in the partition, but we tolerate
// orphan steps (steps before the first header) by collecting them under a
// synthetic null section.
export const groupBySection = (
  items: BDirectRow[],
): Array<{ section: BDirectRow | null; steps: BDirectRow[] }> => {
  const groups: Array<{ section: BDirectRow | null; steps: BDirectRow[] }> = [];
  let current: { section: BDirectRow | null; steps: BDirectRow[] } = {
    section: null,
    steps: [],
  };
  for (const item of items) {
    if (item.kind === 'section') {
      if (current.section || current.steps.length > 0) groups.push(current);
      current = { section: item, steps: [] };
    } else {
      current.steps.push(item);
    }
  }
  if (current.section || current.steps.length > 0) groups.push(current);
  return groups;
};
